import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import API from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await API.get('/auth/me');
      setUser(res.data);
    } catch (err) { console.error('Auth refresh failed:', err); }
  }, []);

  const login = useCallback(async (email, password) => {
    sessionStorage.removeItem('preview_as_client');
    const res = await API.post('/auth/login', { email, password });
    // Admin 2FA flow: backend returns {requires_2fa, temp_token} instead of a session
    if (res.data && res.data.requires_2fa) {
      return { requires_2fa: true, temp_token: res.data.temp_token };
    }
    // Admin first-login without 2FA yet: backend refuses to issue a session and returns
    // a single-use setup_token — user must configure 2FA before getting a JWT.
    if (res.data && res.data.requires_2fa_setup) {
      return { requires_2fa_setup: true, setup_token: res.data.setup_token };
    }
    const me = await API.get('/auth/me');
    setUser(me.data);
    return me.data;
  }, []);

  const verify2fa = useCallback(async (tempToken, code) => {
    const res = await API.post('/auth/2fa/verify', { temp_token: tempToken, code });
    const me = await API.get('/auth/me');
    setUser(me.data);
    return { ...me.data, backup_codes_remaining: res.data?.backup_codes_remaining };
  }, []);

  const register = useCallback(async (data) => {
    sessionStorage.removeItem('preview_as_client');
    const res = await API.post('/auth/register', data);
    const me = await API.get('/auth/me');
    setUser(me.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    sessionStorage.removeItem('preview_as_client');
    try {
      await API.post('/auth/logout');
    } catch (err) { console.error('Logout request failed:', err); }
    setUser(null);
  }, []);

  // Memoize the context value — see SiteSettingsContext comment. Prevents
  // Suspense from getting stuck on lazy() imports in React 19.
  const contextValue = useMemo(
    () => ({ user, loading, login, verify2fa, register, logout, refreshUser }),
    [user, loading, login, verify2fa, register, logout, refreshUser],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-pulse font-serif text-2xl text-stone-text/30">Ernest &amp; Marguerite</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
